// InstJambTypeTab.js - Jamb types configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  InfoBox,
  InstallationDialog
} from './InstallationCommon';
import { useProductScope } from '../utils/productScope';

const InstJambTypeTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    authFetch('/installation/jamb-types')
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/installation/jamb-types/${editData.id}`, formData, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/installation/jamb-types', formData, 'POST');
        setRows(prevRows => [...prevRows, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this jamb type?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/installation/jamb-types/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'label', headerName: 'Label', width: 200 },
    { field: 'value', headerName: 'Value', width: 200 },
    { field: 'applies_to', headerName: 'Applies To', width: 120 },
    { field: 'active', headerName: 'Active', width: 100, renderCell: (params) => params.value ? 'Yes' : 'No' },
    { field: 'sort_order', headerName: 'Sort Order', width: 100 }
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Jamb Type
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Jamb Type
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedRow}>
          Delete Jamb Type
        </Button>
      </Stack>

      <InfoBox />

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
        />
      )}

      <InstallationDialog
        open={openDialog}
        data={editData}
        title="Jamb Type"
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

export default InstJambTypeTab;
